import {
    ClassicEditor, Essentials, Paragraph, Heading, Bold, Italic, Link, List,
    BlockQuote, Image, ImageUpload, ImageToolbar, ImageStyle, SourceEditing,
    Table, TableToolbar, TableColumnResize
} from 'ckeditor5';
// Imported here (via webpack/css-loader) rather than through application.scss's Sprockets/sassc
// pipeline - ckeditor5.css uses modern CSS media-query range syntax ("width <= 600px") that the
// legacy libsass compiler this app's Sprockets setup uses can't parse, and fails on for every
// request once Sprockets tries to process it, regardless of whether it's @import'd or requested
// standalone.
import 'ckeditor5/ckeditor5.css';
import { csrfToken } from './csrf_token';

// Uploads through the server (ActiveStorage/GCS-backed, see PagesController#ckeditor_upload)
// rather than a direct-to-GCS upload, so no bucket CORS configuration is needed here.
class ServerProxyUploadAdapter {
    constructor(loader) {
        this.loader = loader;
    }

    upload() {
        return this.loader.file.then(file => new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('upload', file);

            const xhr = new XMLHttpRequest();
            this.xhr = xhr;
            xhr.open('POST', '/pages/ckeditor_upload', true);
            xhr.responseType = 'json';
            xhr.setRequestHeader('X-CSRF-Token', csrfToken());

            xhr.addEventListener('load', () => {
                const response = xhr.response;
                if (!response || response.error) {
                    reject(response?.error?.message || 'Upload failed');
                    return;
                }
                resolve({ default: response.url });
            });
            xhr.addEventListener('error', () => reject('Upload failed'));
            xhr.addEventListener('abort', () => reject());
            xhr.upload.addEventListener('progress', (evt) => {
                if (evt.lengthComputable) {
                    this.loader.uploadTotal = evt.total;
                    this.loader.uploaded = evt.loaded;
                }
            });

            xhr.send(formData);
        }));
    }

    abort() {
        this.xhr?.abort();
    }
}

function ServerProxyUploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new ServerProxyUploadAdapter(loader);
}

function initializeCkeditor5() {
    document.querySelectorAll('.ckeditor5').forEach((textarea) => {
        if (textarea.dataset.ckeditor5Initialized) return;
        textarea.dataset.ckeditor5Initialized = 'true';

        ClassicEditor.create(textarea, {
            licenseKey: 'GPL',
            plugins: [
                Essentials, Paragraph, Heading, Bold, Italic, Link, List,
                BlockQuote, Image, ImageUpload, ImageToolbar, ImageStyle, SourceEditing,
                Table, TableToolbar, TableColumnResize
            ],
            toolbar: [
                'undo', 'redo', '|', 'heading', '|', 'bold', 'italic', 'link', '|',
                'bulletedList', 'numberedList', 'blockQuote', '|', 'insertTable', 'uploadImage', '|',
                'sourceEditing'
            ],
            table: {
                contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
            },
            // CKEditor 5's Heading plugin defaults to h2-h4 only (treats h1 as reserved for a
            // page's own title element) - this app's existing page content uses h1 for its main
            // heading, so without this the editor silently downgrades every h1 to h2 the moment
            // an admin opens Source view and switches back.
            heading: {
                options: [
                    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
                ]
            },
            image: {
                toolbar: ['imageStyle:inline', 'imageStyle:block', 'imageStyle:side']
            },
            extraPlugins: [ServerProxyUploadAdapterPlugin]
        }).catch((error) => console.error('CKEditor 5 failed to initialize:', error));
    });
}

// Called eagerly below (not only from all_pages.js's turbo:load handler) because this module is
// loaded as an ES module, which browsers always defer relative to HTML parsing - by the time the
// ~7MB ckeditor5 chunk finishes fetching/parsing, Turbo's own turbo:load for the initial page
// load has often already fired and been missed. Safe to call from both places since it's
// idempotent per-element (guarded by the ckeditor5Initialized dataset flag above).
document.addEventListener('turbo:load', initializeCkeditor5);
initializeCkeditor5();

export { initializeCkeditor5 };
